import React from 'react';

const ProfessionalResumePreview = ({ componentRef, resumeData = {} }) => {
  // Process highlighting markers from AI response
  
  const {
    full_name,
    email,
    phone,
    location,
    websites,
    projects,
    professional_summary,
    relevant_experience,
    education,
    certifications,
    achievements,
    languages,
    interests,
    publications,
    volunteer_work,
    additional_info,
    skills = {},
    key_skills = []
  } = resumeData;

  const {
    programming_languages = [],
    frameworks = [],
    tools = [],
    ai_ml_technologies = []
  } = skills || {};

  // Ensure all skills arrays are properly initialized
  const safeProgrammingLanguages = Array.isArray(programming_languages) ? programming_languages : [];
  const safeFrameworks = Array.isArray(frameworks) ? frameworks : [];
  const safeTools = Array.isArray(tools) ? tools : [];
  const safeAiMlTechnologies = Array.isArray(ai_ml_technologies) ? ai_ml_technologies : [];

  const isUploadedResume = resumeData && Object.keys(resumeData).length > 0 && !resumeData.isTemplate;
  const isTemplateResume = resumeData && resumeData.isTemplate;
  
  // Enhanced logic to detect when to show user data
  const shouldShowUserData = resumeData && (
      !resumeData.isTemplate || 
      resumeData.full_name || 
      resumeData.email || 
      resumeData.phone || 
      resumeData.location ||
      resumeData.websites ||
      resumeData.professional_summary ||
      resumeData.projects ||
      resumeData.education ||
      resumeData.relevant_experience ||
      resumeData.skills ||
      resumeData.key_skills ||
      resumeData.achievements ||
      resumeData.certifications
  );

  // Handle both template and uploaded resume data
  const displaySkills = key_skills.length > 0 || 
    (safeProgrammingLanguages.length > 0 || safeFrameworks.length > 0 || safeTools.length > 0 || safeAiMlTechnologies.length > 0);

  // IMPROVED CONTENT FLOW LOGIC - Better page break decisions
  const calculatePageBreakStrategy = () => {
    let estimatedHeight = 0;
    const A4_HEIGHT_ESTIMATE = 950; // Approximate pixels for A4 in print
    const HEADER_HEIGHT = 100;
    const SECTION_HEADER_HEIGHT = 30;
    const CONTENT_LINE_HEIGHT = 20;
    
    // Header
    estimatedHeight += HEADER_HEIGHT;
    
    // Professional Summary
    if (professional_summary) {
      estimatedHeight += SECTION_HEADER_HEIGHT + CONTENT_LINE_HEIGHT * 2;
    }
    
    // Skills
    if (displaySkills) {
      estimatedHeight += SECTION_HEADER_HEIGHT + CONTENT_LINE_HEIGHT * 2;
    }
    
    // Experience
    if (relevant_experience?.length) {
      estimatedHeight += SECTION_HEADER_HEIGHT;
      relevant_experience.forEach(exp => {
        estimatedHeight += CONTENT_LINE_HEIGHT * 2; // Header
        estimatedHeight += CONTENT_LINE_HEIGHT * (exp.description_points?.length || 2);
      });
    }
    
    // Additional Experience
    if (resumeData.additional_experience?.length) {
      estimatedHeight += SECTION_HEADER_HEIGHT;
      estimatedHeight += CONTENT_LINE_HEIGHT * resumeData.additional_experience.length * 2;
    }
    
    // Education height estimate
    let educationHeight = 0;
    if (education?.length) {
      educationHeight += SECTION_HEADER_HEIGHT;
      education.forEach(edu => {
        educationHeight += CONTENT_LINE_HEIGHT * 3; // Title, location, details
      });
    }
    
    // Achievements height
    let achievementsHeight = 0;
    if (achievements?.length) {
      achievementsHeight += SECTION_HEADER_HEIGHT + (achievements.length * CONTENT_LINE_HEIGHT);
    }
    
    // Certifications height
    let certificationsHeight = 0;
    if (Array.isArray(certifications) && certifications.length > 0) {
      certificationsHeight += SECTION_HEADER_HEIGHT + Math.ceil(certifications.length / 2) * CONTENT_LINE_HEIGHT;
    }
    
    // Project height estimate
    let projectHeight = 0;
    if (projects?.length) {
      projectHeight += SECTION_HEADER_HEIGHT;
      projects.forEach(proj => {
        projectHeight += CONTENT_LINE_HEIGHT * 2; // Header
        if (proj.tech_stack) projectHeight += CONTENT_LINE_HEIGHT;
        if (Array.isArray(proj.description)) {
          projectHeight += proj.description.length * CONTENT_LINE_HEIGHT;
        } else {
          projectHeight += CONTENT_LINE_HEIGHT * 2;
        }
        if (proj.github_link) projectHeight += CONTENT_LINE_HEIGHT;
      });
    }
    
    const firstPageContent = estimatedHeight + educationHeight + achievementsHeight + certificationsHeight;
    const shouldProjectsOverflow = firstPageContent + projectHeight > A4_HEIGHT_ESTIMATE * 1.2;
    
    return {
      shouldProjectsOverflow,
      firstPageContent,
      projectHeight,
      educationHeight,
      achievementsHeight,
      certificationsHeight
    };
  };

  const pageStrategy = calculatePageBreakStrategy();
  const showProjectsOnFirstPage = projects && projects.length > 0 && !pageStrategy.shouldProjectsOverflow;
  const showProjectsOnSecondPage = projects && projects.length > 0 && pageStrategy.shouldProjectsOverflow;

  return (
    <div id="section-to-print" className="resume-preview">
      {/* PAGE 1 - Main Content */}
      <div className="resume-paper professional-template" ref={componentRef} data-page="1">
        <header className="professional-header">
          <h1 className="professional-name">
            {shouldShowUserData && full_name ? full_name : "RYAN MONROE"}
          </h1>
          <p className="professional-contact-info">
            {shouldShowUserData && email ? `Email: ${email}` : "Email: ryanmonroe47@gmail.com"}
            {" | "}
            {shouldShowUserData && phone ? `Phone: ${phone}` : "Phone: (302) 559-2291"}
            {shouldShowUserData && location ? ` | Location: ${location}` : (shouldShowUserData ? "" : " | Location: Wilmington, DE")}
          </p>

          {/* Social links */}
          {(resumeData.github || resumeData.linkedin || resumeData.leetcode || resumeData.hackerrank) && (
            <p className="professional-contact-info social-links">
              {resumeData.github && (
                <>
                  <span className="social-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 0C5.374 0 0 5.373 0 12 0 17.302 3.438 21.8 8.207 23.387c.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" fill="#000000"/>
                    </svg>
                  </span>
                  <a href={resumeData.github} target="_blank" rel="noreferrer" className="social-link">GitHub</a>
                </>
              )}
              {resumeData.linkedin && (
                <>
                  <span className="social-separator">|</span>
                  <span className="social-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#000000"/>
                    </svg>
                  </span>
                  <a href={resumeData.linkedin} target="_blank" rel="noreferrer" className="social-link">LinkedIn</a>
                </>
              )}
              {resumeData.hackerrank && (
                <>
                  <span className="social-separator">|</span>
                  <span className="social-icon">
                    <img src="/hakerranklogo.jpeg" alt="HackerRank" className="social-icon-img" />
                  </span>
                  <a href={resumeData.hackerrank} target="_blank" rel="noreferrer" className="social-link">HackerRank</a>
                </>
              )}
              {resumeData.leetcode && (
                <>
                  <span className="social-separator">|</span>
                  <span className="social-icon">
                    <img src="/leetcodelogo.jpeg" alt="LeetCode" className="social-icon-img" />
                  </span>
                  <a href={resumeData.leetcode} target="_blank" rel="noreferrer" className="social-link">LeetCode</a>
                </>
              )}
            </p>
          )}

          {/* Websites/Links */}
          {websites && websites.length > 0 && (
            <p className="professional-contact-info websites-links">
              {websites.map((site, index) => (
                <a key={index} href={site} target="_blank" rel="noreferrer">
                  {site.replace(/^https?:\/\//, '').replace(/^www\./, '')}
                </a>
              ))}
            </p>
          )}
        </header>

        {/* Professional Summary */}
        {(() => {
          const defaultSummaryText = "Experienced professional with strong technical skills and proven track record in software development. Excellent problem-solving abilities and attention to detail. Strong communication and teamwork skills. Adaptable and quick learner with passion for continuous improvement.";
          const pickDisplay = (value, fallback = '') => {
            const trimmed = typeof value === 'string' ? value.trim() : '';
            if (trimmed) return trimmed;
            return resumeData?.isTemplate !== false ? fallback : '';
          };
          const summaryContent = pickDisplay(professional_summary, defaultSummaryText);
          return summaryContent ? (
            <section className="professional-section">
              <h2 className="professional-section-title">PROFESSIONAL SUMMARY</h2>
              <p className="professional-section-content">{summaryContent}</p>
            </section>
          ) : null;
        })()}

        {/* Skills */}
        {displaySkills && (
          <section className="professional-section">
            <h2 className="professional-section-title">SKILLS</h2>
            {key_skills.length > 0 ? (
              // Template skills (key_skills array)
              <div className="skills-3-column">
                {key_skills.map((skill, index) => (
                  <span key={index}>{skill}</span>
                ))}
              </div>
            ) : (
              // Uploaded resume skills (categorized)
              <div className="skills-categorized">
                {[{ label: "Programming Languages", items: safeProgrammingLanguages },
                  { label: "Frameworks", items: safeFrameworks },
                  { label: "Tools", items: safeTools },
                  { label: "AI/ML Technologies", items: safeAiMlTechnologies }]
                  .filter(({ items }) => items.length > 0)
                  .map(({ label, items }) => (
                    <div key={label} className="skill-category">
                      {label}: {items.map((item, idx) => (
                        <span key={idx}>{idx > 0 ? ', ' : ''}{item}</span>
                      ))}
                    </div>
                  ))}
              </div>
            )}
          </section>
        )}

        {/* Experience */}
        {relevant_experience && relevant_experience.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">RELEVANT EXPERIENCE</h2>
            {relevant_experience.map((exp, i) => (
              <div key={i} className="professional-experience-item">
                <div className="professional-experience-header">
                  <div className="professional-experience-title">
                    {exp.position} | {exp.company}{exp.location ? `, ${exp.location}` : ""}
                  </div>
                  <div className="professional-experience-dates">
                    {exp.start_date} – {exp.end_date}
                  </div>
                </div>
                <ul className="professional-experience-points">
                  {(exp.description_points || []).map((pt, j) => <li key={j}>{pt}</li>)}
                </ul>
              </div>
            ))}
          </section>
        )}

        {/* Additional Experience */}
        {resumeData.additional_experience && resumeData.additional_experience.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">ADDITIONAL EXPERIENCE</h2>
            {resumeData.additional_experience.map((exp, i) => (
              <div key={i} className="professional-experience-item">
                <div className="professional-experience-header">
                  <div className="professional-experience-title">
                    {exp.position} | {exp.company}{exp.location ? `, ${exp.location}` : ""}
                  </div>
                  <div className="professional-experience-dates">
                    {exp.start_date} – {exp.end_date}
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}



        {/* Education - ALWAYS on first page, avoid page breaks */}
        {education && education.length > 0 && (
          <section className="professional-section keep-together">
            <h2 className="professional-section-title">EDUCATION</h2>
            {education.map((edu, i) => (
              <div key={i} className="professional-education-item keep-together">
                <div className="professional-education-header">
                  <div className="professional-experience-title">
                    {edu.degree} in {edu.field_of_study}
                  </div>
                  <div className="professional-experience-dates">
                    {(edu.start_year && edu.end_year) ? `${edu.start_year} – ${edu.end_year}` : (edu.graduation_date || '')}
                  </div>
                </div>
                <div className="education-details">
                  {edu.institute || edu.institution}{edu.location ? `, ${edu.location}` : ""}
                  {(edu.cgpa || edu.gpa) && <><br />CGPA: {edu.cgpa || edu.gpa}</>}
                  {edu.minor && <><br />Minor: {edu.minor}</>}
                  {(edu.coursework && edu.coursework.length > 0) && <><br />Relevant Coursework: {Array.isArray(edu.coursework) ? edu.coursework.join(', ') : edu.coursework}</>}
                  {edu.key_coursework && <><br />Key Coursework: {edu.key_coursework}</>}
                  {edu.awards_honors && <><br />Awards & Honors: {edu.awards_honors}</>}
                  {edu.extracurricular && <><br />Extracurricular Activities: {edu.extracurricular}</>}
                </div>
              </div>
            ))}
          </section>
        )}

        {/* Achievements - ALWAYS continue after Education */}
        {achievements && achievements.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">ACHIEVEMENTS</h2>
            <ul className="professional-achievements-list">
              {achievements.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </section>
        )}

        {/* Certifications - ALWAYS continue after Achievements */}
        {Array.isArray(certifications) && certifications.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">CERTIFICATIONS</h2>
            <ul
              className="certifications-grid-list"
              style={{ 
                listStyleType: 'disc', 
                paddingLeft: '20px', 
                margin: 0,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '8px'
              }}
            >
              {certifications.map((c, i) => {
                const label =
                  typeof c === 'string'
                    ? c
                    : [c?.name || c?.title || c?.certification, c?.provider]
                        .filter(Boolean)
                        .join(' — ');
                return <li key={i} className="certification-item">{label}</li>;
              })}
            </ul>
          </section>
        )}

        {/* Projects - ALWAYS show after certifications */}
        {projects && projects.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">PROJECTS</h2>
            {projects.map((proj, i) => (
              <div key={i} className="professional-project-item">
                <div className="professional-project-header">
                  <div className="professional-project-title-container">
                    <h3 className="professional-project-title">
                      {proj.title || proj.name}
                    </h3>
                    {proj.github_link && (
                      <a href={proj.github_link} target="_blank" rel="noreferrer" className="professional-project-github-link">
                        GitHub
                      </a>
                    )}
                  </div>
                  {proj.year && (
                  <div className="professional-project-year">
                    ({proj.year})
                  </div>
                  )}
                </div>
                {proj.tech_stack && proj.tech_stack.length > 0 && (
                  <p className="professional-project-tech">
                    <em>Tech Stack:</em> {Array.isArray(proj.tech_stack) ? proj.tech_stack.map((item, idx) => (
                      <span key={idx}>{idx > 0 ? ', ' : ''}{item}</span>
                    )) : proj.tech_stack}
                  </p>
                )}
                <ul className="professional-project-points">
                  {Array.isArray(proj.description) 
                    ? proj.description.map((d, j) => <li key={j}>{d}</li>) 
                    : <li>{proj.description}</li>
                  }
                </ul>
              </div>
            ))}
          </section>
        )}

        {/* Languages - NEW SECTION */}
        {languages && languages.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">LANGUAGES</h2>
            <ul className="professional-achievements-list">
              {languages.map((lang, i) => {
                const label = typeof lang === 'string' 
                  ? lang 
                  : `${lang.language}${lang.proficiency ? ` (${lang.proficiency})` : ''}`;
                return <li key={i}>{label}</li>;
              })}
            </ul>
          </section>
        )}

        {/* Interests - NEW SECTION */}
        {interests && interests.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">INTERESTS</h2>
            <p style={{ margin: '5px 0', lineHeight: '1.6' }}>
              {interests.join(' • ')}
            </p>
          </section>
        )}

        {/* Publications - NEW SECTION */}
        {publications && publications.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">PUBLICATIONS</h2>
            <ul className="professional-achievements-list">
              {publications.map((pub, i) => <li key={i}>{pub}</li>)}
            </ul>
          </section>
        )}

        {/* Volunteer Work - NEW SECTION */}
        {volunteer_work && volunteer_work.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">VOLUNTEER WORK</h2>
            <ul className="professional-achievements-list">
              {volunteer_work.map((vol, i) => <li key={i}>{vol}</li>)}
            </ul>
          </section>
        )}

        {/* Additional Information - NEW SECTION */}
        {additional_info && additional_info.length > 0 && (
          <section className="professional-section">
            <h2 className="professional-section-title">ADDITIONAL INFORMATION</h2>
            <ul className="professional-achievements-list">
              {additional_info.map((info, i) => <li key={i}>{info}</li>)}
            </ul>
          </section>
        )}
      </div>

      {/* CSS for better print layout */}
      <style jsx>{`
        .resume-paper {
          width: var(--a4-width) !important;
          max-width: var(--a4-width) !important;
          min-height: var(--a4-height) !important;
          margin: 0 auto !important;
          padding: var(--resume-padding-vertical) var(--resume-padding-horizontal) !important;
          box-sizing: border-box !important;
          background: white !important;
          font-size: 11pt !important;
          line-height: 1.4 !important;
        }
        
        .keep-together {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        
        .professional-education-item {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          margin-bottom: 12px !important;
        }
        
        .page-break-before {
          page-break-before: always !important;
          break-before: page !important;
        }
        
        .professional-section {
          margin-bottom: 16px !important;
        }
        
        .professional-section-title {
          font-size: 12pt !important;
          font-weight: bold !important;
          margin-bottom: 8px !important;
          padding-bottom: 2px !important;
          border-bottom: 1px solid #000 !important;
        }
        
        .certifications-grid-list {
          column-count: 2 !important;
          column-gap: 20px !important;
          column-fill: balance !important;
        }
        
        .certification-item {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
          margin-bottom: 4px !important;
          font-size: 10pt !important;
        }
        
        /* Social Links Styling */
        .social-links {
         
          align-items: center !important;
          flex-wrap: wrap !important;
          gap: 0 !important;
        }
        
        .social-icon {
          font-size: 14px !important;
          margin-right: 0px !important;
          color: #000000 !important;
          display: inline-flex !important;
          align-items: center !important;
          vertical-align: middle !important;
        }
        
        .social-icon svg {
          width: 14px !important;
          height: 14px !important;
        }
        
        .social-icon-img {
          width: 18px !important;
          height: 18px !important;
          object-fit: contain !important;
          vertical-align: middle !important;
          display: inline-block !important;
        }
        
        .social-separator {
          margin: 0 8px !important;
          color: #000000 !important;
          font-weight: normal !important;
        }
        
        .social-link {
          color: #000000 !important;
          text-decoration: underline !important;
          text-decoration-color: #000000 !important;
          font-weight: normal !important;
          margin-right: 0 !important;
        }
        
        .social-link:hover {
          color: #000000 !important;
          text-decoration: underline !important;
        }
        
        /* Project GitHub Link Styling */
        .professional-project-title-container {
          display: flex !important;
          align-items: center !important;
          gap: 12px !important;
        }
        
        .professional-project-github-link {
          color: #000000 !important;
          text-decoration: underline !important;
          font-size: 12pt !important;
          font-weight: normal !important;
          margin-left: 8px !important;
        }
        
        .professional-project-github-link:hover {
          color: #000000 !important;
          text-decoration: underline !important;
        }
        
        @media print {
          @page {
            size: A4;
            margin: 0;
            background: white;
          }
          
          html, body {
            background: white !important;
            margin: 0;
            padding: 0;
          }
          
          #section-to-print {
            background: white !important;
          }
          
          .resume-preview {
            background: white !important;
          }
          
          .resume-paper {
            width: var(--a4-print-width) !important;
            max-width: var(--a4-print-width) !important;
            min-height: var(--a4-print-height) !important;
            margin: 0 auto !important;
            padding: var(--resume-padding-vertical) var(--resume-padding-horizontal) !important;
            box-shadow: none !important;
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .page-break-before {
            page-break-before: always !important;
          }
          
          .keep-together {
            page-break-inside: avoid !important;
          }
          
          .professional-education-item {
            page-break-inside: avoid !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfessionalResumePreview;
